import { useTranslation, type Translate } from '@client-app/i18n'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AuthStatusPanel, AuthStatusShell } from './AuthStatusShell'

interface ErrorConfig {
    title: string
    description: string
    toastMessage?: string
    showRetry: boolean
    actionText?: string
    actionLink?: string
}

interface ErrorConfigDefinition {
    titleKey: string
    descriptionKey: string
    toastMessageKey?: string
    showRetry: boolean
    actionTextKey?: string
    actionLink?: string
}

const errorConfigDefinitions: Record<string, ErrorConfigDefinition> = {
    otp_expired: {
        titleKey: 'website.authError.otpExpired.title',
        descriptionKey: 'website.authError.otpExpired.description',
        showRetry: false
    },
    access_denied: {
        titleKey: 'website.authError.accessDenied.title',
        descriptionKey: 'website.authError.accessDenied.description',
        showRetry: false
    },
    email_not_confirmed: {
        titleKey: 'website.authError.emailNotConfirmed.title',
        descriptionKey: 'website.authError.emailNotConfirmed.description',
        showRetry: true,
        actionTextKey: 'website.authError.emailNotConfirmed.action',
        actionLink: '/auth?mode=resend-verification'
    },
    invalid_credentials: {
        titleKey: 'website.authError.invalidCredentials.title',
        descriptionKey: 'website.authError.invalidCredentials.description',
        showRetry: true,
        actionTextKey: 'website.authError.invalidCredentials.action',
        actionLink: '/auth'
    },
    shopify_development_store_blocked: {
        titleKey: 'website.authError.shopifyDevelopmentStoreBlocked.title',
        descriptionKey: 'website.authError.shopifyDevelopmentStoreBlocked.description',
        toastMessageKey: 'website.authError.shopifyDevelopmentStoreBlocked.toast',
        showRetry: false
    },
    default: {
        titleKey: 'website.authError.default.title',
        descriptionKey: 'website.authError.default.description',
        showRetry: true,
        actionTextKey: 'website.authError.default.action',
        actionLink: '/auth'
    }
}

const buildErrorConfig = (definition: ErrorConfigDefinition, t: Translate): ErrorConfig => ({
    title: t(definition.titleKey),
    description: t(definition.descriptionKey),
    toastMessage: definition.toastMessageKey ? t(definition.toastMessageKey) : undefined,
    showRetry: definition.showRetry,
    actionText: definition.actionTextKey ? t(definition.actionTextKey) : undefined,
    actionLink: definition.actionLink,
})

export default function ErrorPage() {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const resolveErrorConfig = useCallback((key: string) => (
        buildErrorConfig(errorConfigDefinitions[key] || errorConfigDefinitions.default, t)
    ), [t])
    const [errorConfig, setErrorConfig] = useState<ErrorConfig>(() => resolveErrorConfig('default'))
    const [errorDescription, setErrorDescription] = useState('')
    const [isPopupErrorRedirect] = useState(() => new URLSearchParams(location.search).has('msg'))
    const didParseErrorRef = useRef(false)

    useEffect(() => {
        if (didParseErrorRef.current) {
            return
        }
        didParseErrorRef.current = true
        const searchParams = new URLSearchParams(location.search)
        const errorCode = searchParams.get('error_code')
        const errorDesc = searchParams.get('error_description')
        const error = searchParams.get('error')
        const msg = searchParams.get('msg')
        const errorKey = errorCode || msg || error

        const hasKnownConfig = Boolean(errorKey && errorConfigDefinitions[errorKey])
        const config = resolveErrorConfig(hasKnownConfig ? errorKey! : 'default')

        setErrorConfig(config)

        if (!hasKnownConfig && errorDesc) {
            setErrorDescription(decodeURIComponent(errorDesc))
        } else if (error && !hasKnownConfig) {
            setErrorDescription(decodeURIComponent(error))
        }

        if (msg) {
            try {
                const channel = new BroadcastChannel('connect')
                const toastMessage = config.toastMessage || config.description
                channel.postMessage({
                    type: 'ERROR',
                    provider: 'shopify',
                    code: msg,
                    message: toastMessage
                })
                channel.close()
                setTimeout(() => {
                    window.close();
                }, 250)
            } catch {
                // Keep rendering the error page if BroadcastChannel is unavailable.
            }
        }

        // 清除 URL 中的错误参数
        if (errorCode || errorDesc || error || msg) {
            const cleanUrl = window.location.pathname
            window.history.replaceState({}, '', cleanUrl)
        }
    }, [resolveErrorConfig])

    const handleAction = () => {
        if (errorConfig.actionLink) {
            navigate(errorConfig.actionLink)
        }
    }

    const handleGoBack = () => {
        navigate('/auth')
    }

    if (isPopupErrorRedirect) {
        return <div className="h-screen bg-white" aria-label={t('website.authError.closingConnectionWindow')} />
    }

    return (
        <AuthStatusShell>
            <AuthStatusPanel>
                <div className="mb-2 text-red-500">
                    <svg
                        width="80"
                        height="80"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                    >
                        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                        <path
                            d="M12 8V12"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                        />
                        <circle cx="12" cy="16" r="1" fill="currentColor" />
                    </svg>
                </div>

                <h2 className="m-0 text-2xl font-semibold text-gray-900">
                    {errorConfig.title}
                </h2>

                <p className="m-0 text-base text-gray-600">
                    {errorDescription || errorConfig.description}
                </p>

                <div className="mt-2 flex w-full max-w-xs flex-col gap-3">
                    {errorConfig.showRetry && errorConfig.actionText && (
                        <button
                            onClick={handleAction}
                            className="h-11 w-full rounded-xl bg-gray-900 px-6 font-medium text-white transition-colors hover:bg-gray-700"
                        >
                            {errorConfig.actionText}
                        </button>
                    )}

                    <button
                        onClick={handleGoBack}
                        className="h-11 w-full rounded-xl border border-gray-200 bg-transparent px-6 font-medium text-gray-900 transition-all hover:border-gray-300 hover:bg-gray-50"
                    >
                        {t('website.authError.backToSignIn')}
                    </button>
                </div>
            </AuthStatusPanel>
        </AuthStatusShell>
    )
}
